import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const LoginPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithGoogle } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleCallback = useCallback(async (response) => {
    if (response.access_token) {
      const result = await loginWithGoogle(response.access_token);
      if (result.success) {
        toast.success('Welcome to InvoiceFlow!');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Login failed');
      }
    }
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    // Load Google OAuth script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google && GOOGLE_CLIENT_ID) {
        window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'email profile',
          callback: handleGoogleCallback,
        });
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, [handleGoogleCallback]);

  const handleGoogleLogin = () => {
    if (!GOOGLE_CLIENT_ID) {
      toast.error('Google OAuth not configured. Please set REACT_APP_GOOGLE_CLIENT_ID');
      return;
    }
    
    if (window.google) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'email profile',
        callback: handleGoogleCallback,
      });
      client.requestAccessToken();
    } else {
      toast.error('Google OAuth not loaded');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Logo and Title */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center gap-3 mb-6">
              <img src="/icon.svg" alt="" className="w-12 h-12 rounded-xl" />
              <h1 className="text-3xl font-extrabold tracking-tight text-primary">
                InvoiceFlow
              </h1>
            </div>
            <p className="text-muted-foreground">
              Professional invoicing made simple
            </p>
          </div>

          {/* Login Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">Welcome</CardTitle>
              <CardDescription>
                Sign in with your Google account to get started
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                className="w-full h-12 gap-3 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                <p>10 free invoices per month</p>
                <p className="mt-1">No credit card required</p>
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          <div className="grid grid-cols-2 gap-4 text-center text-sm">
            <div className="space-y-1">
              <div className="font-semibold text-primary">AI Extraction</div>
              <p className="text-muted-foreground text-xs">Paste text, get invoices</p>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-primary">PDF Export</div>
              <p className="text-muted-foreground text-xs">Professional templates</p>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-primary">Auto-save</div>
              <p className="text-muted-foreground text-xs">Customers & items</p>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-primary">Blind Storage</div>
              <p className="text-muted-foreground text-xs">Nobody who stores it can read it</p>
            </div>
          </div>

          {/* Storage, stated honestly */}
          <div id="storage" className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left text-xs text-muted-foreground leading-relaxed">
            <p className="font-semibold text-primary mb-1">Where your invoices live</p>
            <p>
              InvoiceFlow runs exclusively on{' '}
              <a href="https://blindrange.dev" target="_blank" rel="noreferrer" className="underline text-blue-600 hover:text-blue-700">
                blindrange
              </a>
              {' '}— your invoices are stored encrypted across a network of machines
              that cannot read what they hold. There is no central database to breach
              or subpoena; the app reads from an encrypted local mirror at local speed.
              Stated honestly: our server still processes your data in memory to render
              PDFs and run AI extraction, and the storage network can observe write
              timing, sizes, and coarse hashed buckets — never an amount, a name, or a
              date. We publish what leaks instead of calling it &ldquo;zero knowledge&rdquo;.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:flex flex-1 login-bg relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1770321428579-4d1ddcdf0225?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBnZW9tZXRyaWMlMjBzaGFwZXMlMjB3aGl0ZSUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzcwODQ1NDA0fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/30 to-slate-900/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-bold mb-2">Create Professional Invoices in Seconds</h2>
          <p className="text-lg text-white/80">
            Paste an email or text, let AI extract the details, and generate beautiful PDF invoices instantly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
